import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useLocale } from '@/contexts/LocaleContext'
import { useEdition } from '@/hooks/useEdition'
import { projectService } from '@/services/projectService'
import type { Project } from '@/services/projectTypes'
import { dashboardService, type DashboardData } from '@/services/dashboardService'
import { emotionAnalysisService, type ProjectEmotionSummary } from '@/services/emotionAnalysisService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import KpiCard from '@/components/researcher/KpiCard'
import NewStudyCard from '@/components/researcher/NewStudyCard'
import NeedsAttentionCard from '@/components/researcher/NeedsAttentionCard'
import RecentActivityCard from '@/components/researcher/RecentActivityCard'
import StudiesTable, { type StudyRow } from '@/components/researcher/StudiesTable'
import DashboardSkeleton from '@/components/researcher/DashboardSkeleton'
import DashboardEmpty from '@/components/researcher/DashboardEmpty'
import { toStudyRow } from '@/components/researcher/studyRows'
import type {
  ActivityEvent,
  KpiDatum,
  NeedsAttentionItem,
} from '@/components/researcher/dashboardTypes'

/** "+2" / "−4" style signed number for KPI delta labels. */
const signed = (v: number) => (v > 0 ? `+${v}` : String(v))

const deltaKind = (v: number | null | undefined): KpiDatum['deltaKind'] =>
  v == null || v === 0 ? 'neutral' : v > 0 ? 'pos' : 'neg'

export default function ResearcherDashboard() {
  const theme = useTheme()
  const { t } = useTranslation('dashboard')
  const { language } = useLocale()
  const { user } = useAuth()
  const { isBasic } = useEdition()
  const navigate = useNavigate()

  const [projects, setProjects] = useState<Project[] | null>(null)
  // undefined = loading, null = failed (render with derived fallbacks)
  const [dash, setDash] = useState<DashboardData | null | undefined>(undefined)
  // null while the per-project emotion summaries are still loading.
  const [emotions, setEmotions] = useState<Record<string, ProjectEmotionSummary> | null>(null)

  useEffect(() => {
    let cancelled = false
    projectService
      .getProjects()
      .then((data) => !cancelled && setProjects(data))
      .catch(() => !cancelled && setProjects([]))
    dashboardService
      .getDashboard()
      .then((data) => !cancelled && setDash(data))
      .catch(() => !cancelled && setDash(null))
    emotionAnalysisService
      .summaries()
      .then((data) => !cancelled && setEmotions(data))
      .catch(() => !cancelled && setEmotions({}))
    return () => {
      cancelled = true
    }
  }, [])

  const formatters = useMemo(() => {
    const dateFmt = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' })
    const fullDateFmt = new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long' })
    const timeFmt = new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit' })
    const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' })
    const relative = (iso: string) => {
      const ts = iso ? new Date(iso).getTime() : NaN
      if (!Number.isFinite(ts)) return '—'
      const diffMs = Date.now() - ts
      const mins = Math.round(diffMs / 60000)
      if (mins < 60) return rtf.format(-mins, 'minute')
      const hours = Math.round(mins / 60)
      if (hours < 24) return rtf.format(-hours, 'hour')
      return rtf.format(-Math.round(hours / 24), 'day')
    }
    return { dateFmt, fullDateFmt, timeFmt, relative }
  }, [language])

  const greetingName = user?.first_name || user?.email?.split('@')[0] || ''
  const now = new Date()
  const greetingKey = now.getHours() < 12 ? 'morning' : now.getHours() < 18 ? 'afternoon' : 'evening'
  const dateLabel = `${formatters.fullDateFmt.format(now)} · ${formatters.timeFmt.format(now)}`

  const goToProjects = () => navigate('/projects')
  const goToDemo = () => navigate('/camera')
  const goToWizard = () => navigate('/studies/new')

  const loading = projects === null || dash === undefined
  const isEmpty = !loading && projects.length === 0

  const rows = useMemo<StudyRow[]>(
    () =>
      (projects ?? []).map((p) => ({
        ...toStudyRow(p, formatters.dateFmt.format, formatters.relative),
        emotion: emotions ? emotions[String(p.id)] ?? null : undefined,
      })),
    [projects, emotions, formatters],
  )

  const counts = useMemo(
    () => ({
      all: rows.length,
      active: rows.filter((r) => r.status === 'active' || r.status === 'paused').length,
      completed: rows.filter((r) => r.status === 'done' || r.status === 'review').length,
      drafts: rows.filter((r) => r.status === 'draft').length,
    }),
    [rows],
  )

  // KPI cards from /projects/dashboard/; if the call failed, fall back to what
  // can be derived from the project list alone.
  const kpis = useMemo<KpiDatum[]>(() => {
    if (!dash) {
      return [
        { key: 'activeStudies', value: String(counts.active), delta: '—', deltaKind: 'neutral', spark: [], icon: 'video' },
        { key: 'responsesThisWeek', value: '—', delta: '—', deltaKind: 'neutral', spark: [], icon: 'people' },
        { key: 'avgCompletion', value: '—', delta: '—', deltaKind: 'neutral', spark: [], icon: 'check' },
      ]
    }
    const { active_studies, responses_this_week, avg_completion } = dash.kpis
    return [
      {
        key: 'activeStudies',
        value: String(active_studies.value),
        delta: t('kpi.deltaActiveStudies', { value: signed(active_studies.delta) }),
        deltaKind: deltaKind(active_studies.delta),
        spark: active_studies.spark,
        icon: 'video',
      },
      {
        key: 'responsesThisWeek',
        value: String(responses_this_week.value),
        delta:
          responses_this_week.delta_pct == null
            ? '—'
            : t('kpi.deltaResponses', { value: signed(responses_this_week.delta_pct) }),
        deltaKind: deltaKind(responses_this_week.delta_pct),
        spark: responses_this_week.spark,
        icon: 'people',
      },
      {
        key: 'avgCompletion',
        value: avg_completion.value == null ? '—' : String(avg_completion.value),
        suffix: avg_completion.value == null ? undefined : '%',
        delta:
          avg_completion.delta_pp == null
            ? '—'
            : t('kpi.deltaCompletion', { value: signed(avg_completion.delta_pp) }),
        deltaKind: deltaKind(avg_completion.delta_pp),
        spark: avg_completion.spark,
        icon: 'check',
      },
    ]
  }, [dash, counts.active, t])

  const attentionItems = useMemo<NeedsAttentionItem[]>(
    () =>
      (dash?.needs_attention ?? []).map((it) => ({
        id: it.id,
        name: it.name,
        reasonKey: it.reason,
        detail: t(`needsAttention.detail.${it.reason}`, {
          pct: it.detail.pct ?? 0,
          n: it.detail.n ?? 0,
          days: it.detail.days ?? 0,
          target: it.detail.target ?? 0,
        }),
        status: it.status,
        actionKey: it.action,
      })),
    [dash, t],
  )

  const activityEvents = useMemo<ActivityEvent[]>(
    () =>
      (dash?.recent_activity ?? []).map((e) => ({
        id: e.id,
        who: e.who,
        verbKey: e.kind,
        count: e.count,
        target:
          e.kind === 'reachedRespondents' && e.target_n != null
            ? t('recentActivity.goal', { n: e.target_n })
            : e.target,
        minutesAgo: Math.max(0, Math.round((Date.now() - new Date(e.at).getTime()) / 60000)),
      })),
    [dash, t],
  )

  const c = theme.affexy.colors

  return (
    <ResearcherShell active="dashboard" crumbs={[t('nav.dashboard')]} onPrimaryAction={goToWizard}>
      {loading ? (
        <DashboardSkeleton />
      ) : isEmpty ? (
        <DashboardEmpty
          name={greetingName}
          dateLabel={dateLabel}
          onCreateFirst={goToWizard}
          onTryDemo={goToDemo}
        />
      ) : (
        <Stack spacing={3} sx={{ maxWidth: 1340, mx: 'auto', px: { xs: 2, md: 0 } }}>
          {/* Greeting + KPI row */}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
              <Stack spacing={0.5}>
                <Typography sx={{ fontSize: 12, color: c.ink56, letterSpacing: '0.04em' }}>{dateLabel}</Typography>
                <Typography variant="h1" sx={{ fontSize: { xs: 24, md: 32 } }}>
                  {t(`greeting.${greetingKey}`, { name: greetingName })}
                </Typography>
              </Stack>
              <Button
                color="inherit"
                component="a"
                href="/docs/instrukcja_facewise.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: c.ink72, fontSize: 13, whiteSpace: 'nowrap' }}
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              >
                {t('docs')}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1.35fr 1fr 1fr 1fr' }, gap: 1.75 }}>
              <NewStudyCard onStart={goToWizard} />
              {kpis.map((k) => (
                <KpiCard
                  key={k.key}
                  label={t(`kpi.${k.key}`)}
                  value={k.value}
                  suffix={k.suffix}
                  delta={k.delta}
                  deltaKind={k.deltaKind}
                  spark={k.spark}
                  icon={k.icon}
                />
              ))}
            </Box>
          </Stack>

          {/* Needs attention + recent activity */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: { xs: 2, md: 3 } }}>
            <NeedsAttentionCard
              items={attentionItems}
              onItemAction={(item) => {
                // /studies/:id/results is the Pro-only analytics mock-up.
                // FaceWise reads its results on the study screen itself.
                if (!isBasic && item.actionKey === 'viewResults') navigate(`/studies/${item.id}/results`)
                else if (item.actionKey === 'resume') navigate(`/studies/new?draft=${item.id}`)
                else navigate(`/studies/${item.id}`)
              }}
              onShowAll={goToProjects}
            />
            {/* The card stays — the dashboard is the same in both editions —
                but the global activity feed behind "show all" is Pro-only, and
                a link that bounces straight back to /dashboard is worse than
                no link. */}
            <RecentActivityCard
              events={activityEvents}
              onShowAll={isBasic ? undefined : () => navigate('/activity')}
            />
          </Box>

          {/* Studies table (real data) */}
          <StudiesTable
            rows={rows}
            counts={counts}
            onRowAction={(row) => {
              if (!isBasic && (row.status === 'review' || row.status === 'done'))
                navigate(`/studies/${row.id}/results`)
              else if (row.status === 'draft') navigate(`/studies/new?draft=${row.id}`)
              else navigate(`/studies/${row.id}`)
            }}
          />
        </Stack>
      )}
    </ResearcherShell>
  )
}
